import { Box } from "ui/box/box";
import { CloseButton } from "ui/close-button/close-button";
import { mergeSx } from "ui/merge-sx";
import { Typography } from "ui/typography/typography";
import {
  DEFAULT_CLOSE_LABEL,
  SUBTITLE_VARIANT,
  TITLE_VARIANT,
} from "ui/panel/panel.constants";
import {
  CONTENT_SX,
  CORNER_SX,
  DIVIDER_SX,
  getRootSx,
  HEADER_SX,
  SUBTITLE_SX,
  TITLE_SX,
  TITLE_WITH_CORNER_SX,
} from "ui/panel/panel.styles";
import { type PanelProps } from "ui/panel/panel.types";
import { usePanel } from "ui/panel/use-panel";

export const Panel = ({
  title,
  subtitle,
  onClose,
  closeLabel = DEFAULT_CLOSE_LABEL,
  action,
  divider = true,
  width = "100%",
  children,
  sx,
  ref,
  ...rest
}: PanelProps) => {
  const { hasHeader, hasAction, hasClose, hasCorner, titleId } = usePanel({
    title,
    subtitle,
    onClose,
    action,
  });

  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      {...rest}
      ref={ref}
      sx={mergeSx(getRootSx(width), sx)}>
      {hasHeader && (
        <Box sx={mergeSx(HEADER_SX, divider ? DIVIDER_SX : undefined)}>
          {title && (
            <Typography
              id={titleId}
              component="h2"
              variant={TITLE_VARIANT}
              sx={hasCorner ? TITLE_WITH_CORNER_SX : TITLE_SX}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography component="div" variant={SUBTITLE_VARIANT} sx={SUBTITLE_SX}>
              {subtitle}
            </Typography>
          )}
          {hasAction && <Box sx={CORNER_SX}>{action}</Box>}
          {hasClose && <CloseButton aria-label={closeLabel} onClick={onClose} sx={CORNER_SX} />}
        </Box>
      )}
      {children && <Box sx={CONTENT_SX}>{children}</Box>}
    </Box>
  );
};
